import { ComponentType, CSSProperties, FC, SVGProps } from 'react';
import { CountryIcon } from '@monorepo/react-components';
import BtcIcon from '../assets/Btc.svg?react';
import EthIcon from '../assets/Eth.svg?react';
import GoldIcon from '../assets/Gold.svg?react';
import {
  StyledFlagCircle,
  StyledFlagPrimary,
  StyledFlagSecondary,
  StyledStatusAbbreviation,
  StyledStatusCircle,
} from '../styled/community-trades-table.styled';
import {
  getSymbolFallbackLabel,
  getSymbolFlags,
  getSymbolIcon,
} from '../utils/forex-flags.utils';

const ASSET_ICON_COMPONENTS: Record<
  string,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  Gold: GoldIcon,
  Btc: BtcIcon,
  Eth: EthIcon,
};

const DEFAULT_SIZE = 28;
const FLAG_OVERLAP = 8;
const VERTICAL_OFFSET = 6;

interface ForexFlagIconProps {
  symbol: string;
  abbreviation: string;
  /** Diameter in px for the icon/circle. Defaults to 28. */
  size?: number;
}

/**
 * ForexFlagIcon
 *
 * For forex pairs (e.g. EURUSD) renders two overlapping country flags clipped
 * inside a circle, matching the Figma "currency circle" design:
 *   - Primary (top-left quadrant): base currency flag
 *   - Secondary (bottom-right quadrant): quote currency flag
 *
 * For non-forex symbols (crypto, indices) falls back to the text abbreviation circle.
 * Pass `size` to scale the icon (default 28px).
 */
export const ForexFlagIcon: FC<ForexFlagIconProps> = ({
  symbol,
  abbreviation,
  size = DEFAULT_SIZE,
}) => {
  const iconName = getSymbolIcon(symbol);

  if (iconName) {
    const AssetIcon = ASSET_ICON_COMPONENTS[iconName];
    if (AssetIcon) {
      return <AssetIcon width={size} height={size} />;
    }
  }

  const flags = getSymbolFlags(symbol);

  if (!flags || !flags.secondary) {
    const fallbackLabel = getSymbolFallbackLabel(symbol, abbreviation);
    const isNumeric = fallbackLabel !== abbreviation;
    return (
      <StyledStatusCircle
        isNumeric={isNumeric}
        style={{ width: size, height: size }}
      >
        <StyledStatusAbbreviation isNumeric={isNumeric}>
          {fallbackLabel}
        </StyledStatusAbbreviation>
      </StyledStatusCircle>
    );
  }

  // Make the CountryIcon fill its circular container fully.
  // The flag SVG is 3:2 ratio; scaling to 100% height will cover the circle width.
  const flagStyle: CSSProperties = {
    width: '100%',
    height: '100%',
  };

  const flagSize = size * (20 / DEFAULT_SIZE);

  return (
    <StyledFlagCircle
      style={{
        width: flagSize + FLAG_OVERLAP,
        height: flagSize + VERTICAL_OFFSET,
      }}
    >
      <StyledFlagPrimary style={{ width: flagSize, height: flagSize }}>
        <CountryIcon code={flags.primary} bordered={false} style={flagStyle} />
      </StyledFlagPrimary>
      <StyledFlagSecondary style={{ width: flagSize, height: flagSize }}>
        <CountryIcon
          code={flags.secondary}
          bordered={false}
          style={flagStyle}
        />
      </StyledFlagSecondary>
    </StyledFlagCircle>
  );
};
